import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const isLanding = location.pathname === "/";
  const overHero = false;
  const linkBase = "text-sm transition-colors";
  const linkColor = "text-muted-foreground hover:text-foreground";

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        overHero
          ? "bg-transparent"
          : scrolled
            ? "backdrop-blur-md bg-white/80 border-b border-border"
            : "bg-transparent"
      }`}
    >
      <div className="container flex h-20 items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <Link to="/" aria-label="Torflash Startseite">
            <Logo variant={overHero ? "light" : "default"} />
          </Link>
        </div>

        {/* Desktop */}
        <nav className="hidden items-center gap-6 md:flex">
          <button onClick={() => scrollTo("hero-demo")} className={`${linkBase} ${linkColor}`}>So funktioniert's</button>
          <button onClick={() => scrollTo("preise")} className={`${linkBase} ${linkColor}`}>Preise</button>
          <button onClick={() => scrollTo("faq")} className={`${linkBase} ${linkColor}`}>FAQ</button>

          <span className={`mx-1 h-5 w-px ${overHero ? "bg-white/20" : "bg-border"}`} />

          {user ? (
            <>
              <Link to="/mein-verein" className={`${linkBase} ${overHero ? "text-white hover:text-white" : "text-foreground hover:text-primary"}`}>Mein Verein</Link>
              <button onClick={handleSignOut} className={`${linkBase} ${linkColor}`}>Abmelden</button>
            </>
          ) : (
            <>
              <Link to="/login" className={`${linkBase} ${overHero ? "text-white hover:text-white" : "text-foreground hover:text-primary"}`}>Einloggen</Link>
              <Link to="/signup"><Button variant="navCta" size="sm">Jetzt starten →</Button></Link>
            </>
          )}
        </nav>

        <button className={`md:hidden ${overHero ? "text-white" : "text-foreground"}`} onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-background p-4 md:hidden">
          <nav className="flex flex-col gap-3">
            <button onClick={() => scrollTo("hero-demo")} className="text-left text-sm text-muted-foreground">So funktioniert's</button>
            <button onClick={() => scrollTo("preise")} className="text-left text-sm text-muted-foreground">Preise</button>
            <button onClick={() => scrollTo("faq")} className="text-left text-sm text-muted-foreground">FAQ</button>
            {user ? (
              <>
                <Link to="/mein-verein" onClick={() => setOpen(false)} className="text-sm text-foreground">Mein Verein</Link>
                <button onClick={() => { setOpen(false); handleSignOut(); }} className="text-left text-sm text-muted-foreground">Abmelden</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-foreground">Einloggen</Link>
                <Link to="/signup" onClick={() => setOpen(false)}><Button variant="hero" size="sm" className="w-full">Jetzt starten →</Button></Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
